import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import AuthService from '../AuthService/AuthService';
import ApiError from '../../types/ApiError';

/**
 * Custom axios instance that should be used for all services.
 *
 * This file configures Axios with the correct base URL for the API.
 *
 * It also adds interceptors which automatically add auth headers to requests,
 * and handle authentication errors returned from the API.
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL
});

// If a stored token is present, add it as the Authorization header to
// all API requests.
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = AuthService.getToken();
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = token ? `Bearer ${token}` : '';
    return config;
  }
);

// If the response from the API is an 401 error (i.e. their token is invalid or expired),
// then handle the authentication failure, logout, and redirect to the login page.
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: any): Promise<any> => {
    // Catch 401 errors and force a logout, but exclude auth endpoints and the
    // user identity check — those handle their own errors.
    if (
      error.name !== 'CanceledError' &&
      error.response?.status === 401 &&
      error.config.url !== '/users/me' &&
      !error.config.url?.startsWith('/auth/')
    ) {
      AuthService.logout().catch(() => {});
    }

    // Try to parse the error message into an ApiError so
    // that we can display useful information to the user.
    try {
      const apiError = error.response.data as ApiError;
      apiError.timestamp = new Date(apiError.timestamp);
      // eslint-disable-next-line no-param-reassign
      error.apiError = apiError;
      return Promise.reject(error);
    } catch (parseError) {
      // Do not append the API error to the error message
    }

    return Promise.reject(error);
  }
);

export default api;
